// SHA-256 over a Blob/File using the SubtleCrypto API.
// Reports progress as the file is read — good UX for large files.

export async function sha256File(
  file: Blob,
  onProgress?: (bytesHashed: number, total: number) => void,
): Promise<string> {
  // SubtleCrypto does not expose a streaming digest, so we buffer the file
  // into memory and hash it whole. For files up to 100 MB this fits
  // comfortably in memory on any modern device.
  const total = file.size;
  const buf = new Uint8Array(new ArrayBuffer(total));
  let offset = 0;
  const reader = file.stream().getReader();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf.set(value, offset);
    offset += value.byteLength;
    onProgress?.(offset, total);
  }
  return digestHex(buf.buffer as ArrayBuffer);
}

export async function sha256Bytes(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  // Always copy into a fresh, plain ArrayBuffer to satisfy TS's strict
  // BufferSource typing (which disallows SharedArrayBuffer-backed views).
  const src = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const copy = new Uint8Array(new ArrayBuffer(src.byteLength));
  copy.set(src);
  return digestHex(copy.buffer as ArrayBuffer);
}

async function digestHex(ab: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", ab);
  return toHex(new Uint8Array(digest));
}

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

// Deterministic JSON: sorted keys, no whitespace. Matches the server's canonicalStringify.
export function canonicalStringify(obj: unknown): string {
  if (obj === null || typeof obj !== "object") return JSON.stringify(obj);
  if (Array.isArray(obj)) return "[" + obj.map(canonicalStringify).join(",") + "]";
  const keys = Object.keys(obj as Record<string, unknown>).sort();
  return "{" + keys.map(k =>
    JSON.stringify(k) + ":" + canonicalStringify((obj as Record<string, unknown>)[k])
  ).join(",") + "}";
}
